import React from 'react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Instagram, Shield, CheckCircle2, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { consumeInstagramConnectionConfirmation } from '../../utils/instagramConnect';
import { getFullOnboardingStatus, ONBOARDING_STATUS } from '../../utils/onboardingStatus';
import './PendingApproval.scss';

const PendingApproval = () => {
  const { t } = useTranslation('creator');
  const { currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = React.useState(true);
  const [connectionConfirmation, setConnectionConfirmation] = React.useState(null);

  const isCreator = currentUser?.user_type === 'creator';
  const roleLabel = isCreator ? t('pending_approval.role_creator', { defaultValue: 'creator' }) : t('pending_approval.role_business', { defaultValue: 'brand' });
  const profile = isCreator ? currentUser?.creator_profile : currentUser?.business_profile;
  const onboardingStatus = getFullOnboardingStatus(currentUser);
  const isApproved = onboardingStatus === ONBOARDING_STATUS.APPROVED;
  const isSubmitted = onboardingStatus === ONBOARDING_STATUS.PENDING_ADMIN;
  const isRejected = onboardingStatus === ONBOARDING_STATUS.REJECTED;
  const igConnected = profile?.instagram_account_connected;
  const approvedDestination = isCreator ? '/creator/feed' : '/business/collabs';

  React.useEffect(() => {
    const confirmation = consumeInstagramConnectionConfirmation(window.location.pathname);
    if (confirmation) {
      setConnectionConfirmation(confirmation);
    }
  }, []);

  // Poll for status update or check on mount
  React.useEffect(() => {
    const checkStatus = async () => {
      if (refreshUser) {
        await refreshUser();
      }
      setChecking(false);
    };

    checkStatus();
    
    // Check every 30 seconds if submitted
    let interval;
    if (isSubmitted) {
      interval = setInterval(checkStatus, 30000);
    }
    
    return () => clearInterval(interval);
  }, [refreshUser, isSubmitted]);

  React.useEffect(() => {
    if (isApproved) {
      navigate(approvedDestination, { replace: true });
    }
  }, [approvedDestination, isApproved, navigate]);

  React.useEffect(() => {
    if (
      !checking
      && currentUser
      && [ONBOARDING_STATUS.NEEDS_PROFILE, ONBOARDING_STATUS.NEEDS_INSTAGRAM].includes(onboardingStatus)
    ) {
      navigate('/complete-profile', {
        replace: true,
        state: { returnTo: location.pathname },
      });
    }
  }, [checking, currentUser, location.pathname, navigate, onboardingStatus]);

  if (checking && !currentUser) return null;
  if (isApproved || [ONBOARDING_STATUS.NEEDS_PROFILE, ONBOARDING_STATUS.NEEDS_INSTAGRAM].includes(onboardingStatus)) return null;

  return (
    <div className="pending-approval-page">
      <motion.div
        className="pending-approval-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className={`status-icon ${isRejected ? 'rejected' : 'pending'}`}>
          {isRejected ? <Shield size={48} /> : <Clock size={48} />}
        </div>

        {connectionConfirmation && igConnected && (
          <div className="instagram-confirmation-panel">
            <CheckCircle2 size={18} />
            <div>
              <strong>{t('pending_approval.instagram_confirmed_title')}</strong>
              <p>{t('pending_approval.instagram_confirmed_body')}</p>
            </div>
          </div>
        )}

        <h1 className="pending-title">
          {isRejected
            ? t('pending_approval.title_rejected')
            : isSubmitted
              ? t('pending_approval.title_submitted', { role: roleLabel })
              : t('pending_approval.title_pending')}
        </h1>

        <p className="pending-description">
          {isRejected ? (
            <>
              {t('pending_approval.desc_rejected')}
              {profile?.rejection_reason && (
                <span className="rejection-reason">
                   <strong>{t('pending_approval.rejection_reason')}</strong> {profile.rejection_reason}
                </span>
              )}
            </>
          ) : isSubmitted ? (
            t('pending_approval.desc_submitted', { role: roleLabel })
          ) : (
            t('pending_approval.desc_pending')
          )}
        </p>

        {isSubmitted && (
          <div className="pending-email-notice">
            <Mail size={18} />
            <p>
              {currentUser?.email
                ? t('pending_approval.email_notice_with_address', {
                    email: currentUser.email,
                    defaultValue: `Te enviaremos un correo electrónico a ${currentUser.email} una vez que tu cuenta haya sido aprobada.`,
                  })
                : t('pending_approval.email_notice', {
                    defaultValue: 'Te enviaremos un correo electrónico una vez que tu cuenta haya sido aprobada.',
                  })}
            </p>
          </div>
        )}

        {!isSubmitted && !isRejected && (
          <div className="pending-actions">
            {!igConnected && (
              <div className="action-item">
                <span className="action-icon"><Instagram size={20} /></span>
                <span>{t('pending_approval.action_instagram')}</span>
              </div>
            )}
          </div>
        )}

        {(!isSubmitted || isRejected) && (
          <div className="pending-buttons">
            <button
              className="btn-verify"
              onClick={() => navigate('/complete-profile')}
            >
              {isRejected ? t('pending_approval.btn_reapply') : t('pending_approval.btn_apply')}
            </button>
          </div>
        )}
        {currentUser?.can_change_user_type && (
          <button type="button" className="change-account-type" onClick={() => navigate('/select-role')}>
            {t('auth:role_selection.change_action')}
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default PendingApproval;
